import { z } from 'zod';
import {
  PIPELINE_DEBUG_STEP_INPUT_LIMITS,
  type PipelineStepInputValidationLimits,
} from './pipeline-debug-step-input.limits';
import {
  adieInputSchema,
  formatZodError,
  icsInputSchema,
  rawLogRecordSchema,
} from './pipeline-step-input.schemas';

export type PipelineDebugStepId = 'lin' | 'adie' | 'ics' | 'de';

export const PIPELINE_DEBUG_STEP_IDS = [
  'lin',
  'adie',
  'ics',
  'de',
] as const satisfies readonly PipelineDebugStepId[];

export type { PipelineStepInputValidationLimits } from './pipeline-debug-step-input.limits';

export {
  PIPELINE_DEBUG_MAX_ANOMALY_EVENTS,
  PIPELINE_DEBUG_MAX_DE_EVENTS,
  PIPELINE_DEBUG_MAX_INPUT_BYTES,
  PIPELINE_DEBUG_MAX_LIN_LOGS,
  PIPELINE_DEBUG_MAX_LOG_BATCHES,
  PIPELINE_DEBUG_STEP_INPUT_LIMITS,
} from './pipeline-debug-step-input.limits';

/** @deprecated Use PIPELINE_DEBUG_STEP_INPUT_LIMITS */
export const DEFAULT_PIPELINE_STEP_INPUT_LIMITS =
  PIPELINE_DEBUG_STEP_INPUT_LIMITS;

/** @deprecated Use PIPELINE_DEBUG_MAX_INPUT_BYTES */
export const DEFAULT_PIPELINE_DEBUG_MAX_INPUT_BYTES =
  PIPELINE_DEBUG_STEP_INPUT_LIMITS.maxInputBytes;

const jsonObjectSchema = z.record(z.string(), z.unknown());

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringifyPipelineInput(input: unknown): string {
  const seen = new WeakSet<object>();

  const serialized = JSON.stringify(input, (_key, value: unknown) => {
    if (typeof value === 'bigint') {
      return value.toString();
    }

    if (typeof value === 'function') {
      return '[Function]';
    }

    if (typeof value === 'symbol') {
      return value.toString();
    }

    if (value !== null && typeof value === 'object') {
      if (seen.has(value)) {
        return '[Circular]';
      }

      seen.add(value);
    }

    return value;
  });

  return serialized ?? 'null';
}

function measureUtf8Bytes(value: string): number {
  const bufferApi = (
    globalThis as {
      Buffer?: { byteLength?: (input: string, encoding?: string) => number };
    }
  ).Buffer;

  if (typeof bufferApi?.byteLength === 'function') {
    return bufferApi.byteLength(value, 'utf8');
  }

  return new TextEncoder().encode(value).byteLength;
}

export function measurePipelineDebugInputBytes(input: unknown): number {
  return measureUtf8Bytes(stringifyPipelineInput(input));
}

function buildLinInputSchema(maxLinLogs: number) {
  return z.array(rawLogRecordSchema).min(1).max(maxLinLogs);
}

function buildAdieInputSchema(maxLogBatches: number) {
  return adieInputSchema.extend({
    logs: z.array(jsonObjectSchema).max(maxLogBatches),
  });
}

function buildIcsInputSchema(maxLogBatches: number, maxAnomalyEvents: number) {
  return icsInputSchema.extend({
    logs: z.array(jsonObjectSchema).max(maxLogBatches),
    anomaly_events: z.array(jsonObjectSchema).max(maxAnomalyEvents),
  });
}

function buildDeInputSchema(maxDeEvents: number) {
  const deIncidentInputSchema = z
    .object({
      incident_id: z.string().min(1),
      events: z.array(jsonObjectSchema).min(1).max(maxDeEvents),
    })
    .passthrough();

  return z.preprocess((value) => {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return value;
    }

    const record = value as Record<string, unknown>;
    const incidentId =
      typeof record.incident_id === 'string'
        ? record.incident_id
        : typeof record.incidentId === 'string'
          ? record.incidentId
          : undefined;
    const events = Array.isArray(record.events)
      ? record.events
      : Array.isArray(record.Events)
        ? record.Events
        : undefined;

    return {
      ...record,
      incident_id: incidentId,
      events,
    };
  }, deIncidentInputSchema);
}

export function validatePipelineStepInput(
  stepId: PipelineDebugStepId,
  input: unknown,
  limits: PipelineStepInputValidationLimits = PIPELINE_DEBUG_STEP_INPUT_LIMITS,
): { success: true } | { success: false; error: string } {
  if (input === undefined) {
    return { success: false, error: 'input is required' };
  }

  let byteSize: number;
  try {
    byteSize = measurePipelineDebugInputBytes(input);
  } catch {
    return { success: false, error: 'input is not JSON-serializable' };
  }

  if (byteSize > limits.maxInputBytes) {
    return {
      success: false,
      error: `input exceeds maximum size of ${limits.maxInputBytes} bytes`,
    };
  }

  if (stepId === 'lin') {
    const parsed = buildLinInputSchema(limits.maxLinLogs).safeParse(
      Array.isArray(input) ? input : [input],
    );
    return parsed.success
      ? { success: true }
      : { success: false, error: formatZodError(parsed.error) };
  }

  if (stepId === 'adie') {
    const parsed = buildAdieInputSchema(limits.maxLogBatches).safeParse(input);
    return parsed.success
      ? { success: true }
      : { success: false, error: formatZodError(parsed.error) };
  }

  if (stepId === 'ics') {
    const parsed = buildIcsInputSchema(
      limits.maxLogBatches,
      limits.maxAnomalyEvents,
    ).safeParse(input);
    return parsed.success
      ? { success: true }
      : { success: false, error: formatZodError(parsed.error) };
  }

  const record = asRecord(input);
  if (
    record &&
    (record.skipped === true ||
      Array.isArray(record.results) ||
      Array.isArray(record.routing))
  ) {
    return {
      success: false,
      error:
        'ICS did not produce an incident with events. Check ICS results for errors.',
    };
  }

  const parsed = buildDeInputSchema(limits.maxDeEvents).safeParse(input);
  return parsed.success
    ? { success: true }
    : { success: false, error: formatZodError(parsed.error) };
}

export {
  adieInputSchema,
  deInputSchema,
  formatZodError,
  icsInputSchema,
  linInputSchema,
  rawLogRecordSchema,
} from './pipeline-step-input.schemas';
